import React, { useState, useContext, useCallback } from 'react'
import { useNavigate, Routes, Route } from 'react-router-dom'
import { AuthContext } from '@/layout/Auth'
import { PlainCard } from '../components'
import RoleCreate from './createForm'
import { ColumnAction } from '@/components/Table'
import { Row } from '../components'
import Table, { pageProps } from '@/components/Table'
import { ConfirmButton } from '@/components/Button'
import { RoleInfo } from '@/types'
import { roleColumns } from './column'
import { KeywordSearch } from '@/components/Search'
import Editor from './editor'
import { confirm } from '@/components/Modal'
import { useQuery, useMutation } from '@tanstack/react-query'
import { getDetailRols, deleteRole } from '@/api'
import { useDebounce } from '@/hooks'
import ErrorBoundary from '@/layout/Error'
import { ensureSecurityGate } from '@/utils/securityGate'

const Member: React.FC<unknown> = () => {
  const navigate = useNavigate()

  const { authInfo } = useContext(AuthContext)
  const orgInfo = authInfo?.orgInfo

  const [total, setTotal] = useState<number>(0)
  const [page, setPage] = useState<number>(1)
  const [data, setData] = useState<RoleInfo[]>([])
  const [rows, setRows] = useState<number>(10)

  const [keyword, setKeyword] = useState<string>('')
  const debounceInput = useDebounce(keyword, 500)
  // @ts-ignore
  const [editing, setEditing] = useState<RoleInfo>(null)

  const queryDetailRols = useQuery(
    ['getDetailRols', orgInfo?.id, page, rows, debounceInput],
    () =>
      // @ts-ignore
      orgInfo?.id &&
      getDetailRols({
        id: orgInfo?.id,
        query: { page, size: rows, name: debounceInput },
      }),
    {
      onSuccess: (res) => {
        // @ts-ignore
        setTotal(res.total)
        // @ts-ignore
        setData(res.data)
      },

      refetchOnMount: true,
      enabled: !!orgInfo?.id,
    },
  )

  const deleteRoleMutation = useMutation(deleteRole, {
    onSuccess: () => {
      queryDetailRols.refetch()
    },
  })

  const handlePageChange = (value: pageProps) => {
    setPage(value.page)
    // @ts-ignore
    setRows(value.rowsPerPage)
  }

  const gotoEdit = useCallback(() => {
    navigate('/console/organization/role/editRole')
  }, [navigate])

  const columnsAction = useCallback(
    (action: ColumnAction, data: RoleInfo) => () => {
      switch (action) {
        case 'delete': {
          confirm({
            title: '提示',
            content: `确认将角色"${data.name}"从空间"${orgInfo?.name}"中移除`,
            // @ts-ignore
            onClose: null,
            onConfirm: async () => {
              if (!(await ensureSecurityGate('admin_operation'))) return
              deleteRoleMutation.mutate({
                // @ts-ignore
                id: orgInfo?.id,
                role_ids: [data.id],
              })
            },
            closeAfterClose: true,
          })
          break
        }
        case 'edit': {
          ;(async () => {
            if (!(await ensureSecurityGate('admin_operation'))) return
            setEditing(data)
            gotoEdit()
          })()
          break
        }
      }
    },
    [deleteRoleMutation, gotoEdit, orgInfo?.id, orgInfo?.name],
  )

  const gotoCreate = useCallback(() => {
    ;(async () => {
      if (!(await ensureSecurityGate('admin_operation'))) return
      navigate('/console/organization/role/createRole')
    })()
  }, [navigate])

  const gotoList = useCallback(() => {
    // @ts-ignore
    setEditing(null)
    orgInfo?.id && queryDetailRols.refetch()
    navigate('/console/organization/role')
  }, [navigate, orgInfo?.id, queryDetailRols])

  const handleRefetchRoles = useCallback(() => {
    queryDetailRols.refetch()
  }, [queryDetailRols])

  return (
    <PlainCard>
      <Routes>
        <Route
          path='/'
          errorElement={<ErrorBoundary />}
          element={
            <>
              <Row sx={{ justifyContent: 'flex-end', gap: '24px' }}>
                <KeywordSearch
                  placeholder='搜索角色名'
                  clear={true}
                  value={keyword}
                  onChange={setKeyword}
                  // @ts-ignore
                  onSearch={null}
                />
                <ConfirmButton onClick={gotoCreate}>创建角色</ConfirmButton>
              </Row>
              <Table<RoleInfo>
                sx={{ flex: 1, overflow: 'hidden' }}
                columns={roleColumns(columnsAction)}
                data={data}
                rowKey={'id'}
                pagination={{
                  page,
                  total,
                  onChange: handlePageChange,
                  rowsPerPage: rows,
                }}
              />
            </>
          }
        />
        <Route path='createRole' errorElement={<ErrorBoundary />} element={<RoleCreate onCancel={gotoList} />} />
        <Route
          path='editRole'
          errorElement={<ErrorBoundary />}
          element={<Editor roleInfo={editing} onCancel={gotoList} refetch={handleRefetchRoles} />}
        />
      </Routes>
    </PlainCard>
  )
}

export default Member
